import React, { useEffect, useState, useRef } from 'react'
import _get from 'lodash/get'
import Grid from '@material-ui/core/Grid'
import { elementScroll } from '../../../common/helpers'
import { CustomTitle, LocationDetailWrapper } from './styles'
import { useAuth } from '../../../auth/useAuth'
import PAID_LEAVE from '../../constants'
import detailsService from './detailsService'
import {
  getTopicsByFilters,
  getFilteredJurisdictions,
  getFilteredNavItems,
  getFilteredProducts,
  getFilteredSummaries,
  getMostGranularJurisdiction
} from './filters'
import APILoadingSpinnerRenderer from '../../../app/components/apiLoadingSpinner'
import LocationContent from './components/LocationContent'
import DetailsNavigation from './components/DetailsNavigation'
import TopicsDetailComponent from './components/TopicsDetailComponent'
import DefaultErrorMessage from '../../../common/components/ErrorBoundary/errorMessage'
import { usePaidLeave } from '../../../context/paidLeaveContext'

export const getProductById = (searchId) => PAID_LEAVE.PRODUCTS
  .find(({ id: productId }) => productId === searchId)

export const mapLocationData = (location) => {
  const flatMapOfTopics = location
    .locales
    .flatMap((jurisdiction) => jurisdiction.topics)

  const jurisdictions = location.locales.map(({ id, name, state, jurisdiction, country }) => ({
    id,
    name,
    state,
    jurisdiction,
    country
  }))

  const locationTopics = location.topics.map((locationTopic) => {
    const topicTarget = flatMapOfTopics.find((topic) => topic.id === locationTopic.id)
    const jurisdiction = jurisdictions.find(({ id }) => topicTarget.jurisdiction === id)
    return (topicTarget && jurisdiction)
      ? {
        ...locationTopic,
        jurisdiction,
        product: getProductById(locationTopic.product)
      }
      : locationTopic
  })


  return {
    ...location,
    topics: locationTopics,
    jurisdictions
  }
}

export const mapToLegacyTopics = (topics) => {
  return topics.flatMap((topic) => topic.subTopics.flatMap((subtopic) => subtopic.contentBlocks.flatMap((contentBlock) => ({
    id: topic._id,
    jurisdictionUniqueName: 'beef', // part of the legacy contract but apparently unused. leaving it here to avoid confusion, this is deprecated on arrival anyway
    active: true, // should only be returning active topics from db
    topic: topic.title,
    product: topic.product,
    jurisdiction: topic.jurisdiction,
    industry: contentBlock.industry && contentBlock.industry.name,
    locale_id: topic.jurisdiction,
    sub_topic: subtopic.title,
    extra: {
      Context: contentBlock.content,
      'Law Citation': contentBlock.law,
      'Regulation Citation': contentBlock.regulation,
      'Primary Link': contentBlock.primaryLink,
      'Secondary Link': contentBlock.secondaryLink
    },
    effective_date: contentBlock.effectiveDate,
    expire_date: contentBlock.expirationDate,
    sub_industry: contentBlock.industry && contentBlock.industry.subIndustry,
    sub_sub_industry: contentBlock.industry && contentBlock.industry.subSubIndustry,
    summary: {
      placeholders: [],
      template: topic.summary && topic.summary.template
    },
    sort_order: 'beef' // intentional beef, see above
  }))))
}

const mapToMongoIshTopics = (detailsApiTopics) => {
  return detailsApiTopics.flatMap((topic) => topic.subTopics.flatMap((subtopic) => subtopic.contentBlocks.flatMap((contentBlock) => ({
    id: topic._id,
    active: true,
    context: contentBlock.content,
    effectiveDate: contentBlock.effectiveDate,
    expireDate: contentBlock.expirationDate,
    industry: contentBlock.industry && contentBlock.industry.name,
    jurisdiction: topic.jurisdiction,
    lawCitation: contentBlock.law,
    links: mapToMongoIshLinks(contentBlock),
    maximumCompanyRevenue: null,
    maximumEmployeeCount: null,
    minimumCompanyRevenue: null,
    minimumEmployeeCount: null,
    notes: null,
    regulationCitation: contentBlock.regulation,
    subIndustry: contentBlock.industry && contentBlock.industry.subIndustry,
    subSubIndustry: contentBlock.industry && contentBlock.industry.subSubIndustry,
    subTopic: subtopic.title,
    topic: topic.title,
    wage: null,
    indexedRate: null,
    sortOrder: 'beef', // intentional beef because i am NOT dealing with any downstream property-of-undefined errors but we don't need this
    product: topic.product,
    summary: {
      placeholders: [],
      template: topic.summary && topic.summary.template
    }
  }))))
}

const mapToMongoIshLinks = (contentBlock) => {
  const result = []
  if (contentBlock.primaryLink) result.push(contentBlock.primaryLink)
  if (contentBlock.secondaryLink) result.push(contentBlock.secondaryLink)

  return result
}

export const mapToLegacyContract = ({ locationApiResult, locationId }) => {
  const locales = locationApiResult.jurisdictions.map((jd) => ({
    id: jd._id,
    name: jd.name,
    jurisdiction: jd.type,
    uniqueName: jd.uniqueName,
    topics: Object.values(jd.products)
      .flatMap((product) => mapToLegacyTopics(product.topics))
  }))

  return {
    id: locationId,
    name: locationApiResult.displayName,
    address1: locationApiResult.address && locationApiResult.address.addressOne,
    address2: locationApiResult.address && locationApiResult.address.addressTwo,
    address3: locationApiResult.address && locationApiResult.address.addressThree,
    city: locationApiResult.address && locationApiResult.address.city,
    state: locationApiResult.address && locationApiResult.address.stateCode,
    zip: locationApiResult.address && locationApiResult.address.zip,
    county: 'beef', // this had better not matter :knife_emoji
    country: 'US', // PL is only in the US anyway and this legacy contract can burn in the eternal flame at the heart of the void
    active: true, // good lord
    locales,
    topics: locationApiResult.jurisdictions.flatMap((jd) => (Object.values(jd.products)
      .flatMap((product) => mapToMongoIshTopics(product.topics))))
  }
}

const PaidLeaveDetails = () => {
  const { selectedProduct } = usePaidLeave()
  const locationId = window.location.hash.split('/')
    .slice(-1)
    .pop()

  const company = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [location, setLocation] = useState({})
  const [topicFilters, setTopicFilters] = useState({
    product: {},
    jurisdiction: {}
  })
  const [focusedTopic, setFocusedTopic] = useState(null)
  const [filteredContent, setFilteredContent] = useState({
    topics: [],
    navItems: [],
    products: [],
    jurisdictions: [],
    summaries: []
  })
  const topicsContainer = useRef()

  const setCurrentJurisdiction = (jurisdiction) => {
    setTopicFilters({
      ...topicFilters,
      jurisdiction
    })
  }

  const getNewJurisdiction = ({ filteredJds, topicFilters, mostGranularJurisdictionForProduct }) => {
    const legacyLogicResult = filteredJds.length > 1
      ? topicFilters.jurisdiction
      : mostGranularJurisdictionForProduct

    return !filteredContent.jurisdictions.some((jd) => jd.id === legacyLogicResult.id)
      ? legacyLogicResult
      : mostGranularJurisdictionForProduct
  }

  const handleChange = (event) => {
    const selectedProduct = getProductById(event.target.value)
    const selectedTopics = location.topics.filter(({ product }) => product.id === selectedProduct.id)
    const mostGranularJurisdictionForProduct = getMostGranularJurisdiction(selectedTopics)
    const filteredJds = handleFilteredContent({
      location,
      topicFilters: {
        product: selectedProduct,
        jurisdiction: mostGranularJurisdictionForProduct
      }
    })
    const newTopicsFilters = {
      product: selectedProduct,
      jurisdiction: getNewJurisdiction({ filteredJds, topicFilters, mostGranularJurisdictionForProduct })
    }

    setFocusedTopic(null)
    setTopicFilters(newTopicsFilters)
    window.scrollTo(0, 0)
    document.getElementById(topicsContainer.current.props.id)
      .scrollTo(0, 0)
  }

  const handleFilteredContent = ({ location, topicFilters }) => {
    let filteredJds = []
    if (_get(location, 'id', false)) {
      filteredJds = getFilteredJurisdictions({
        location,
        topicFilters
      })
      setFilteredContent({
        topics: getTopicsByFilters({
          topics: location.topics,
          topicFilters
        }),
        navItems: getFilteredNavItems({
          location,
          topicFilters
        }),
        products: getFilteredProducts({
          location,
          topicFilters
        }),
        jurisdictions: filteredJds,
        summaries: getFilteredSummaries({
          topics: location.topics,
          topicFilters
        })
      })
    }
    return filteredJds
  }

  const performScroll = (key) => {
    if (elementScroll('scroll-topics-container', `[data-topic="${key}"]`)) {
      setFocusedTopic(key)
    }
  }

  useEffect(() => {
    let subscribed = true
    if (subscribed && company) {
      setLoading(true)
      detailsService.fetchLocation(locationId)
        .then((locationApiResult) => {
          const legacyResult = mapToLegacyContract({ locationApiResult, locationId })
          const locationResult = mapLocationData(legacyResult)
          if (locationResult) {
            const product = selectedProduct
              || getFilteredProducts({
                location: locationResult,
                topicFilters
              })[0]
            const topicsForProduct = locationResult.topics.filter((topic) => topic.product.id === product.id)
            const jurisdiction = getMostGranularJurisdiction(topicsForProduct)
            setTopicFilters({
              jurisdiction,
              product
            })
            setLocation(locationResult)
            handleFilteredContent({
              location: locationResult,
              topicFilters: {
                jurisdiction,
                product
              }
            })
          }
          setLoading(false)
        })
        .catch((fetchLocationError) => {
          console.error(`ERROR in getLocationDetails(): ${fetchLocationError.message} \r ${fetchLocationError.stack}`)
          setLoading(false)
          setError(fetchLocationError)
        })
    }
    return () => {
      subscribed = false
    }
  }, [company])

  const withoutPaper = true

  useEffect(() => {
    let subscribed = true
    if (subscribed) {
      handleFilteredContent({
        location,
        topicFilters
      })
    }
    return () => {
      subscribed = false
    }
  }, [topicFilters])

  if (error) {
    return <DefaultErrorMessage error={error} data-testId="error-container" />
  }

  return loading ? (
    <APILoadingSpinnerRenderer withoutPaper={withoutPaper} />
  ) : (
    <div data-testid="main-container" data-topic="_TOP_">
      <CustomTitle data-testid={location.name}>{location.name}</CustomTitle>
      <Grid container spacing={24}>
        <Grid item xs={9}>
          <LocationDetailWrapper id="scroll-topics-container" ref={topicsContainer}>
            <LocationContent
              {...{
                location,
                applicableJurisdictions: filteredContent.jurisdictions,
                focusedJurisdiction: topicFilters.jurisdiction,
                setCurrentJurisdiction
              }}
            />
            <TopicsDetailComponent {...{
              focusedTopic,
              performScroll,
              topics: filteredContent.topics,
              summaries: filteredContent.summaries,
              currentProduct: topicFilters.product
            }}
            />
          </LocationDetailWrapper>
        </Grid>

        <Grid item xs={3}>

          <DetailsNavigation
            {...{
              topics: filteredContent.navItems,
              products: filteredContent.products,
              currentProduct: topicFilters.product,
              focusedTopic,
              handleChange,
              performScroll
            }}
          />
        </Grid>
      </Grid>
    </div>
  )
}

export default PaidLeaveDetails
